import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ShieldAlert, Church, Loader2, X, CheckCircle } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import authFetch from '../utils/authFetch';

const CONTACT_EMAIL = 'dev@pariscentrechretien.com';

const Screen_DeleteAccount = () => {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const { logout } = useAuth();

  const [showConfirm, setShowConfirm] = useState(false);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(null); // { scheduledFor }

  // After deletion, log out and bounce to login after 5s.
  useEffect(() => {
    if (!done) return;
    const t = setTimeout(() => { logout?.(); navigate('/login'); }, 5000);
    return () => clearTimeout(t);
  }, [done, logout, navigate]);

  const handleDelete = async () => {
    setSubmitting(true);
    setError('');
    try {
      const res = await authFetch('/api/users/me/delete-account', {
        method: 'POST',
        body: JSON.stringify({ reason: reason.trim() || undefined }),
      });
      setShowConfirm(false);
      setDone({ scheduledFor: res?.deletionScheduledFor });
      // Immediately drop the session client-side (backend already blocks it).
      try { await logout?.(); } catch (_) { /* noop */ }
    } catch (e) {
      setError(e?.detail || e?.message || "Une erreur est survenue. Réessayez.");
    } finally {
      setSubmitting(false);
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return 'dans 30 jours';
    try { return new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }); }
    catch { return 'dans 30 jours'; }
  };

  const bg = isDark ? 'bg-[#272727]' : 'bg-gray-50';
  const cardCls = isDark ? 'bg-[#333333] border-[#444444]' : 'bg-white border-gray-100';
  const text = isDark ? 'text-[#F2F1EB]' : 'text-gray-900';
  const muted = isDark ? 'text-gray-400' : 'text-gray-600';

  // ── Post-deletion confirmation screen ──
  if (done) {
    return (
      <div className={`min-h-screen ${bg} flex flex-col items-center justify-center px-6 text-center`}>
        <CheckCircle size={56} className="text-green-500 mb-4" />
        <h1 className={`text-xl font-bold mb-3 ${text}`}>Votre compte a été désactivé</h1>
        <p className={`text-sm mb-2 ${muted}`}>
          Vos données personnelles seront définitivement supprimées le <strong>{fmtDate(done.scheduledFor)}</strong>.
        </p>
        <p className={`text-sm mb-2 ${muted}`}>
          Pour annuler cette demande, contactez <strong>{CONTACT_EMAIL}</strong>.
        </p>
        <p className={`text-sm mb-6 ${muted}`}>Vous restez membre de l'église.</p>
        <p className={`text-xs ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>Redirection vers la connexion…</p>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${bg} pb-10`}>
      {/* Header */}
      <div className={`px-4 pcc-safe-top pb-4 ${isDark ? 'bg-[#2D2D2D]' : 'bg-white'} border-b ${isDark ? 'border-[#444444]' : 'border-gray-100'}`}>
        <button onClick={() => navigate(-1)} className={`flex items-center gap-2 ${muted}`}>
          <ArrowLeft size={22} />
          <span className="font-medium">Retour</span>
        </button>
        <h1 className={`text-2xl font-bold mt-3 ${text}`}>Supprimer mon compte</h1>
      </div>

      <div className="px-4 mt-5 space-y-4">
        {/* Reassurance card — account ≠ leaving the church */}
        <div className={`rounded-2xl border p-4 flex gap-3 ${isDark ? 'bg-[#9398DE]/10 border-[#9398DE]/30' : 'bg-[#9398DE]/5 border-[#9398DE]/20'}`}>
          <Church size={22} className="text-[#9398DE] flex-shrink-0 mt-0.5" />
          <p className={`text-sm ${text}`}>
            <strong>Cela ne vous retire pas de l'église.</strong> Votre fiche membre reste dans le registre
            pastoral. Vous perdez seulement l'accès à l'application.
          </p>
        </div>

        {/* What will happen */}
        <div className={`rounded-2xl border p-4 ${cardCls}`}>
          <h2 className={`text-base font-bold mb-3 ${text}`}>Ce qui va se passer</h2>
          <ul className={`space-y-2 text-sm ${muted}`}>
            <li>• Votre compte sera <strong>désactivé immédiatement</strong>.</li>
            <li>• Vos données personnelles seront <strong>définitivement supprimées après 30 jours</strong>.</li>
            <li>• Pendant ce délai, vous pouvez annuler en contactant <strong>{CONTACT_EMAIL}</strong>.</li>
            <li>• Les notes pastorales écrites par vos référents seront <strong>anonymisées</strong>, car elles font partie du suivi pastoral de l'église.</li>
          </ul>
        </div>

        <button
          onClick={() => { setError(''); setShowConfirm(true); }}
          className="w-full flex items-center justify-center gap-2 py-3.5 rounded-2xl bg-red-600 text-white font-semibold hover:bg-red-700 transition-colors"
          data-testid="delete-account-cta"
        >
          <ShieldAlert size={18} />
          Confirmer la suppression
        </button>
      </div>

      {/* Second confirmation modal */}
      {showConfirm && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4" onClick={() => !submitting && setShowConfirm(false)}>
          <div className={`w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl ${isDark ? 'bg-[#2D2D2D]' : 'bg-white'}`} onClick={(e) => e.stopPropagation()}>
            <div className={`flex items-center justify-between p-4 border-b ${isDark ? 'border-[#444444]' : 'border-gray-100'}`}>
              <h3 className={`text-lg font-bold ${text}`}>Êtes-vous sûr ?</h3>
              <button onClick={() => !submitting && setShowConfirm(false)} className={muted}><X size={22} /></button>
            </div>
            <div className="p-4 space-y-3">
              <p className={`text-sm ${muted}`}>
                Cette action est <strong>réversible pendant 30 jours</strong> mais définitive après.
              </p>
              <p className={`text-sm ${muted}`}>
                Vous restez membre de l'église, seul votre accès à l'app est concerné.
              </p>
              <div>
                <label className={`block text-xs font-medium mb-1 ${muted}`}>Motif (facultatif, aide l'équipe pastorale à s'améliorer)</label>
                <textarea
                  rows={3}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  className={`w-full px-3 py-2.5 rounded-xl border text-sm ${isDark ? 'bg-[#3A3A3A] border-[#444444] text-[#F2F1EB]' : 'bg-gray-50 border-gray-200 text-gray-900'}`}
                  placeholder="Votre message (optionnel)"
                />
              </div>
              {error && <p className="text-sm text-red-500 text-center">{error}</p>}
            </div>
            <div className={`p-4 border-t flex gap-3 ${isDark ? 'border-[#444444]' : 'border-gray-100'}`}>
              <button
                onClick={() => setShowConfirm(false)}
                disabled={submitting}
                className={`flex-1 py-3 rounded-xl font-medium ${isDark ? 'bg-[#3A3A3A] text-[#F2F1EB]' : 'bg-gray-100 text-gray-900'}`}
              >
                Annuler
              </button>
              <button
                onClick={handleDelete}
                disabled={submitting}
                className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-red-600 text-white font-semibold hover:bg-red-700 disabled:opacity-60"
                data-testid="delete-account-confirm"
              >
                {submitting ? <Loader2 size={18} className="animate-spin" /> : null}
                Oui, supprimer mon compte
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Screen_DeleteAccount;
